import {useState, useMemo} from 'react';
import type {Selection} from '@react-types/shared';
import {useMenuTriggerState} from '../../@react-stately/menu';
import {useControlledState} from '../../@react-stately/utils';
import type {Collection} from '../../collections/types';
import type {
  SingleSelectListState,
  SingleSelectionProps,
  MenuTriggerState,
  MenuTriggerProps,
  Expandable,
  TreeState,
} from '../../collections/state/types';
import {useReactAriaTreeState} from '../../collections';

export interface MultiSelectStateOptions {
  getItems: Collection['getItems'];
  disabledKeysArg?: Set<string>;
  selection: SingleSelectionProps;
  trigger: MenuTriggerProps;
  expandable: Expandable;
}

export interface MultiSelectState
  extends SingleSelectListState,
    MenuTriggerState {
  /** Whether the select is currently focused. */
  readonly isFocused: boolean;

  /** Sets whether the select is focused. */
  setFocused(isFocused: boolean): void;
}

/**
 * A replacement for
 * [useSelectState](https://react-spectrum.adobe.com/react-stately/useSelectState.html)
 * Provides state management for a select component. Handles building a collection
 * of items from props, handles the open state for the popup menu, and manages
 * multiple selection state.
 */
export function useMultiSelectState({
  getItems,
  selection,
  trigger,
  expandable,
}: MultiSelectStateOptions): MultiSelectState & TreeState {
  const triggerState = useMenuTriggerState(trigger || {});

  const [selectedKey, setSelectedKey] = useControlledState(
    selection.selectedKey,
    selection.defaultSelectedKey ?? null,
    selection.onSelectionChange,
  );
  const selectedKeys = useMemo(
    () => (selectedKey != null ? [selectedKey] : []),
    [selectedKey],
  );

  const state = useReactAriaTreeState({
    getItems,
    ...selection,
    ...expandable,
    selectionMode: 'single',
    disallowEmptySelection: true,
    selectedKeys,
    onSelectionChange: (keys: Selection) => {
      const key = [...keys][0];

      setSelectedKey(key);

      triggerState.close();
    },
  });

  const [isFocused, setFocused] = useState(false);

  const selectedItem =
    selectedKey != null ? state.collection.getItem(selectedKey) : null;

  return {
    ...state,
    ...triggerState,
    selectedKey: selectedKey as string,
    setSelectedKey,
    selectedItem,
    open() {
      if (state.collection.size !== 0) {
        triggerState.open();
      }
    },
    toggle(focusStrategy) {
      if (state.collection.size !== 0) {
        triggerState.toggle(focusStrategy);
      }
    },
    isFocused,
    setFocused,
  };
}
